import { useEffect, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import { useAuth, useCan } from "../auth/AuthProvider";
import { Modal } from "../components/Modal";
import {
  Alert,
  Button,
  Card,
  CardHeader,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Skeleton,
  StatusBadge,
  Textarea,
} from "../components/ui";
import { ApiError } from "../lib/api";
import { absoluteDay } from "../lib/format";
import { useOrgQuota, useRequestQuota } from "../lib/queries";
import type { QuotaRequest, QuotaResource } from "../lib/types";

/**
 * The organization's ceilings: how many apps, databases and private
 * connections it may have, how many it has, and the way to ask for more.
 *
 * A ceiling is where a developer lands when a create is refused -- the
 * refusal names this page -- so the first thing here is the three numbers
 * and a bar, with nothing to read before them. Asking is an admin's act:
 * a request is the organization speaking to the platform, and what comes
 * back changes what it can be billed for. A developer sees the numbers and
 * who to ask.
 *
 * One open request per resource. The form is a modal preset to the resource
 * whose row it was opened from -- or to the one in `?ask=`, which is how a
 * refused create and the meter beside a New button land here already on the
 * right form. It asks for a number and a reason, and the reason is required
 * at length: a request is read by a person, and "more please" is not
 * something a person can decide.
 */
const PRESET_FACTORS = [2, 5];
const REASON_MAX = 2000;
const RESOURCES: QuotaResource[] = ["apps", "databases", "tunnels", "domains", "custom_domains"];
const RESOURCE_KEYS = new Set<string>(RESOURCES);

/** Where a bar turns from measured to warning to full. */
const tone = (used: number, limit: number): "good" | "warn" | "bad" =>
  limit === 0 || used >= limit ? "bad" : used / limit >= 0.8 ? "warn" : "good";

const BAR: Record<"good" | "warn" | "bad", string> = {
  good: "bg-accent",
  warn: "bg-warn",
  bad: "bg-danger",
};

export function LimitsPage() {
  const { t } = useTranslation("limits");
  const { currentOrg } = useAuth();
  const canAsk = useCan("admin");
  const orgId = currentOrg?.id;
  const quota = useOrgQuota(orgId);
  const request = useRequestQuota(orgId ?? "");

  const [searchParams, setSearchParams] = useSearchParams();
  const [asking, setAsking] = useState<QuotaResource | null>(null);
  const [requestedLimit, setRequestedLimit] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<QuotaResource | null>(null);

  const pending = (resource: QuotaResource) =>
    quota.data?.requests.find((r) => r.resource === resource && r.status === "requested") ?? null;

  const open = (resource: QuotaResource) => {
    const line = quota.data?.resources[resource];
    setAsking(resource);
    setRequestedLimit(line ? String(line.limit * PRESET_FACTORS[0]!) : "");
    setReason("");
    setError(null);
    setSent(null);
  };

  const close = () => {
    setAsking(null);
    if (searchParams.has("ask")) {
      searchParams.delete("ask");
      setSearchParams(searchParams, { replace: true });
    }
  };

  /*
   * `?ask=apps` opens the form for that resource once the numbers are in,
   * unless a request is already waiting -- then the row says so and the
   * form would only be refused. Consumed once, so closing does not reopen.
   */
  const ask = searchParams.get("ask");
  useEffect(() => {
    if (!ask || !RESOURCE_KEYS.has(ask) || !quota.data || !canAsk || asking) return;
    if (pending(ask as QuotaResource)) return;
    open(ask as QuotaResource);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once the data is in
  }, [ask, quota.data, canAsk]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!asking) return;
    setError(null);
    try {
      await request.mutateAsync({ resource: asking, requestedLimit: Number(requestedLimit), reason });
      setSent(asking);
      close();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : t("request.failed"));
    }
  }

  const current = asking ? (quota.data?.resources[asking].limit ?? 0) : 0;

  return (
    <div className="space-y-6">
      {/* Names the organization, as Integrations does: this page is reached
          from the account menu, where the switcher above it is the only
          other thing saying whose ceilings these are. */}
      <PageHeader
        title={t("title")}
        description={
          currentOrg
            ? t("description", { organization: currentOrg.name })
            : t("descriptionNoOrg")
        }
      />

      <Card>
        <CardHeader title={t("ceilings.title")} description={t("ceilings.description")} />
        {sent && (
          <div className="border-b border-line px-5 py-3">
            <Alert tone="success">{t("modal.sent")}</Alert>
          </div>
        )}
        {quota.isPending ? (
          <ul className="divide-y divide-line" aria-busy="true" aria-label={t("skeleton")}>
            {RESOURCES.map((resource) => (
              <li key={resource} className="space-y-3 px-5 py-4">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-1.5 w-full" />
              </li>
            ))}
          </ul>
        ) : quota.error ? (
          <div className="p-5">
            <Alert>{quota.error.message}</Alert>
          </div>
        ) : (
          <ul className="divide-y divide-line">
            {RESOURCES.map((resource) => {
              const line = quota.data!.resources[resource];
              const level = tone(line.used, line.limit);
              const waiting = pending(resource);
              return (
                <li key={resource} className="space-y-3 px-5 py-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-fg">{t(`resources.${resource}`)}</p>
                      <p className="text-xs text-fg-subtle">
                        {t("ceilings.usage", { used: line.used, limit: line.limit })}
                        {line.custom ? ` · ${t("ceilings.custom")}` : ""}
                      </p>
                    </div>
                    {waiting ? (
                      <StatusBadge tone="warn" label={t("ceilings.waiting", { limit: waiting.requestedLimit })} />
                    ) : canAsk ? (
                      <Button variant="secondary" size="sm" onClick={() => open(resource)}>
                        {t("ceilings.ask")}
                      </Button>
                    ) : null}
                  </div>
                  <div
                    className="h-1.5 w-full overflow-hidden rounded-full bg-raised"
                    role="meter"
                    aria-valuemin={0}
                    aria-valuemax={line.limit}
                    aria-valuenow={line.used}
                    aria-label={t(`resources.${resource}`)}
                  >
                    <div
                      className={`h-full rounded-full ${BAR[level]}`}
                      style={{ width: `${line.limit === 0 ? 100 : Math.min(100, (line.used / line.limit) * 100)}%` }}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        {!canAsk && quota.data && (
          <p className="border-t border-line px-5 py-3 text-xs text-fg-subtle">{t("ceilings.adminsAsk")}</p>
        )}
      </Card>

      <Modal
        open={asking !== null}
        onClose={close}
        title={asking ? t("request.title", { resource: t(`resources.${asking}`).toLowerCase() }) : ""}
        description={t("request.description")}
      >
        {asking && (
          <form onSubmit={submit} className="space-y-4" id="quota-request">
            {error && <Alert>{error}</Alert>}
            <Field label={t("request.limit")} hint={t("request.limitHint", { current })} htmlFor="quota-limit">
              <div className="flex flex-wrap items-center gap-2">
                <Input
                  id="quota-limit"
                  type="number"
                  inputMode="numeric"
                  min={current + 1}
                  step={1}
                  value={requestedLimit}
                  onChange={(e) => setRequestedLimit(e.target.value)}
                  className="w-32"
                  required
                  autoFocus
                />
                {/* The common asks as one click each; anything else is typed. */}
                <span className="text-xs text-fg-subtle">{t("modal.presets")}</span>
                {PRESET_FACTORS.map((factor) => (
                  <Button
                    key={factor}
                    type="button"
                    size="sm"
                    variant={Number(requestedLimit) === current * factor ? "secondary" : "ghost"}
                    onClick={() => setRequestedLimit(String(current * factor))}
                  >
                    {current * factor}
                  </Button>
                ))}
              </div>
              {Number(requestedLimit) > current && (
                <p className="mt-1.5 font-mono text-xs text-fg-subtle">
                  {t("modal.preview", { from: current, to: Number(requestedLimit) })}
                </p>
              )}
            </Field>
            <Field
              label={t("request.reason")}
              hint={t("request.reasonHint")}
              htmlFor="quota-reason"
              action={<span className="text-xs text-fg-subtle">{t("modal.chars", { count: reason.length, max: REASON_MAX })}</span>}
            >
              <Textarea
                id="quota-reason"
                rows={4}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                minLength={10}
                maxLength={REASON_MAX}
                required
              />
            </Field>
            <div className="flex flex-wrap justify-end gap-3">
              <Button type="button" variant="ghost" onClick={close}>
                {t("request.cancel")}
              </Button>
              <Button type="submit" loading={request.isPending}>
                {t("request.submit")}
              </Button>
            </div>
          </form>
        )}
      </Modal>

      <Card>
        <CardHeader title={t("history.title")} description={t("history.description")} />
        {quota.data && quota.data.requests.length === 0 ? (
          <EmptyState title={t("history.emptyTitle")} description={t("history.emptyBody")} />
        ) : (
          <ul className="divide-y divide-line">
            {quota.data?.requests.map((item) => (
              <RequestRow key={item.id} item={item} />
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

const STATUS_TONE = { requested: "warn", approved: "good", denied: "bad" } as const;

function RequestRow({ item }: { item: QuotaRequest }) {
  const { t } = useTranslation("limits");
  return (
    <li className="space-y-1.5 px-5 py-4">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="text-sm font-medium text-fg">
          {t("history.line", {
            resource: t(`resources.${item.resource}`),
            from: item.currentLimit,
            to: item.requestedLimit,
          })}
        </span>
        <StatusBadge
          tone={STATUS_TONE[item.status]}
          label={
            item.status === "approved" && item.grantedLimit !== null && item.grantedLimit !== item.requestedLimit
              ? t("history.grantedOther", { limit: item.grantedLimit })
              : t(`history.status.${item.status}`)
          }
        />
        <span className="text-xs text-fg-subtle">{absoluteDay(item.requestedAt)}</span>
      </div>
      <p className="text-[13px] text-fg-muted">{item.reason}</p>
      {item.decisionNote && (
        <p className="text-[13px] text-fg-muted">
          <span className="text-fg-subtle">{t("history.note")}</span> {item.decisionNote}
        </p>
      )}
    </li>
  );
}
